import "./styles.scss";

import { useApp, useQueryDict } from "hooks/app";
import { useMember } from "hooks/member";
import React, { useRef, useState } from "react";
import { FcKey } from "react-icons/fc";
import { Link, useNavigate } from "react-router-dom";
import { logout, logoutAdminMember } from "services/user-service";

import { Avatar, Divider, MenuItem, MenuList, Popper } from "@mui/material";

const TopMenu = () => {
  const navigate = useNavigate();
  const { showAlert } = useApp();

  const anchorRemindersEl = useRef(null);
  const anchorCardsEl = useRef(null);
  const anchorAccountEl = useRef(null);

  const [openReminders, setOpenReminders] = useState(false);
  const [openCards, setOpenCards] = useState(false);
  const [openAccount, setOpenAccount] = useState(false);

  const { headerMessage } = useApp();
  const { showSimpleHeader } = useQueryDict();
  const { initialized, isLoggedIn, avatarImage, adminMemberId, isAdmin, adminRoles, firstName, lastName, fullName, setMember, memberId, getMember } =
    useMember();

  const handleRemindersClick = () => {
    handleRemindersClose();
    navigate("/reminders");
  };

  const handleCardsClick = () => {
    handleCardsClose();
    navigate("/mycards/upcoming");
  };

  const handleRemindersClose = () => {
    setOpenReminders(false);
  };

  const handleRemindersOpen = () => {
    setOpenReminders(true);
    setOpenCards(false);
  };

  const handleCardsClose = () => {
    setOpenCards(false);
  };

  const handleCardsOpen = () => {
    setOpenCards(true);
    setOpenReminders(false);
  };

  const handleAccountClick = () => {
    handleAccountClose();
    navigate("/account");
  };

  const handleAccountClose = () => {
    setOpenAccount(false);
  };

  const handleAccountOpen = () => {
    setOpenAccount(true);
  };

  const onAdminMemberLogout = async () => {
    const { errors } = await logoutAdminMember();
    if (errors?.length > 0) {
      showAlert({ message: "There was a problem logging out admin", type: "error" });
    } else if (memberId) {
      getMember();
      window.location.href = `/admin/member/${memberId}`;
    }
  };

  const onLogout = async () => {
    const { errors } = await logout();
    if (errors?.length > 0) {
      showAlert({ message: "There was a problem logging you out", type: "error" });
    } else {
      setMember({});
      showAlert({ message: "You have been succesfully logged out!" });
    }
  };

  const canShowDashboardLink = adminRoles?.includes("GM") || adminRoles?.includes("CP") || adminRoles?.includes("RV");
  const canShowBuildLink = adminRoles?.includes("GM");

  //logInfo('RENDER: Top Menu Actions');

  if (!initialized) {
    return null;
  }

  if (showSimpleHeader) {
    return (
      <div className="top-menu">
        <div className="top-menu-item">
          <span className="top-menu-item-text">{headerMessage || `Welcome ${fullName || "Guest"}`}</span>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="top-menu">
        <Link to="/reminders/add" alt="Reminders" title="Reminders" className="top-menu-item">
          <span className="top-menu-item-text">Reminders</span>
        </Link>
        <div className="top-menu-item-divider">|</div>
        <Link to="/login" alt="Login" title="Login" className="top-menu-item">
          <span className="top-menu-item-text">Sign In</span>
        </Link>
        <div className="top-menu-item-divider">|</div>
        <Link to="/register" alt="Register" title="Register" className="top-menu-item">
          <span className="top-menu-item-text">Register</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="top-menu">
      <div
        className="top-menu-item"
        ref={anchorRemindersEl}
        aria-controls={openReminders ? "header-reminders-menu" : undefined}
        aria-haspopup="true"
        onMouseEnter={handleRemindersOpen}
        onMouseLeave={handleRemindersClose}
      >
        <span className="top-menu-item-text" onClick={handleRemindersClick}>
          My Reminders
        </span>
        <Popper open={openReminders} anchorEl={anchorRemindersEl.current} role={undefined} disablePortal style={{ zIndex: 1000 }}>
          <MenuList id="header-reminders-menu" className="top-menu-dropdown">
            <MenuItem onClick={handleRemindersClose}>
              <Link to="/reminders">My Reminders</Link>
            </MenuItem>
            <MenuItem onClick={handleRemindersClose}>
              <Link to="/address_book">My Contacts</Link>
            </MenuItem>
            <MenuItem onClick={handleRemindersClose}>
              <Link to="/calendar">My Calendar</Link>
            </MenuItem>
          </MenuList>
        </Popper>
      </div>
      <div className="top-menu-item-divider">|</div>
      <div
        className="top-menu-item"
        ref={anchorCardsEl}
        aria-controls={openCards ? "header-cards-menu" : undefined}
        aria-haspopup="true"
        onMouseEnter={handleCardsOpen}
        onMouseLeave={handleCardsClose}
      >
        <span className="top-menu-item-text" onClick={handleCardsClick}>
          My Cards
        </span>
        <Popper open={openCards} anchorEl={anchorCardsEl.current} role={undefined} disablePortal style={{ zIndex: 1000 }}>
          <MenuList id="header-cards-menu" className="top-menu-dropdown">
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/upcoming">Upcoming</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/sent">Sent</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/signed">Signed</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/received">Received</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/drafts">Drafts</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/favorites">Favorites</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/photo">Photo</Link>
            </MenuItem>
            <MenuItem onClick={handleCardsClose}>
              <Link to="/mycards/gift">Gift Cards</Link>
            </MenuItem>
          </MenuList>
        </Popper>
      </div>
      <div className="top-menu-item-divider">|</div>
      <div
        className="top-menu-item"
        ref={anchorAccountEl}
        aria-controls={openAccount ? "header-account-menu" : undefined}
        aria-haspopup="true"
        onMouseEnter={handleAccountOpen}
        onMouseLeave={handleAccountClose}
      >
        <span className="top-menu-item-text" onClick={handleAccountClick}>
          <span>My Account</span>
          <span className="top-menu-avatar">
            <Avatar src={avatarImage} alt="Avatar Image" className="avatar-image">
              <span style={{ fontSize: 12, fontWeight: "bold", textTransform: "uppercase" }}>
                {firstName ? firstName[0] : ""}
                {lastName ? lastName[0] : ""}
              </span>
            </Avatar>
            {isAdmin && <FcKey className="avatar-indicator" />}
          </span>
        </span>
        <Popper open={openAccount} anchorEl={anchorAccountEl.current} role={undefined} disablePortal style={{ zIndex: 1000 }}>
          <MenuList id="header-account-menu" className="top-menu-dropdown">
            <MenuItem onClick={handleAccountClose}>
              <Link to="/account">Account Settings</Link>
            </MenuItem>
            <MenuItem onClick={handleAccountClose}>
              <Link to="/email_settings">Email Settings</Link>
            </MenuItem>
            <MenuItem onClick={handleAccountClose}>
              <Link to="/notifications">Notifications</Link>
            </MenuItem>
            <MenuItem onClick={handleAccountClose}>
              <Link to="/change_password">Change Password</Link>
            </MenuItem>
            <MenuItem onClick={handleAccountClose}>
              <Link to="/payment">Payment Info</Link>
            </MenuItem>
            <MenuItem onClick={handleAccountClose}>
              {adminMemberId ? (
                <span onClick={onAdminMemberLogout} className="admin-link red">
                  Back to admin
                </span>
              ) : (
                <span onClick={onLogout} className="admin-link">
                  Sign Out
                </span>
              )}
            </MenuItem>
            {isAdmin && <Divider variant="middle" />}
            {isAdmin && (
              <MenuItem onClick={handleAccountClose}>
                <a href="/admin" target="_blank" className="admin-link red">
                  Admin
                </a>
              </MenuItem>
            )}
            {isAdmin && canShowDashboardLink && (
              <MenuItem onClick={handleAccountClose}>
                <a href="/dashboard" target="_blank" className="admin-link red">
                  Dashboard
                </a>
              </MenuItem>
            )}
            {isAdmin && canShowBuildLink && (
              <MenuItem onClick={handleAccountClose}>
                <a href="/admin/build" target="_blank" className="admin-link red">
                  Build
                </a>
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={handleAccountClose}>
                <a href="/testing?page=card-send-control-center" className="admin-link red">
                  Test: Card Control Center
                </a>
              </MenuItem>
            )}
            {isAdmin && (
              <MenuItem onClick={handleAccountClose}>
                <a href="/testing?page=paywall" className="admin-link red">
                  Test: Paywall
                </a>
              </MenuItem>
            )}
            {isAdmin && <Divider variant="middle" />}
            {isAdmin && (
              <div className="version" style={{ fontSize: 11, textAlign: "center", padding: "6px 16px", lineHeight: "20px" }}>
                <i>{global.version}</i>
              </div>
            )}
          </MenuList>
        </Popper>
      </div>
    </div>
  );
};

TopMenu.propTypes = {};

TopMenu.defaultProps = {};

export default TopMenu;
